// mapFilterEngine.ts
export const columnIds = ["a", "su", "ss", "sp", "l"] as const;
export const rowIds = ["p", "u", "f", "future", "n", "s", "h"] as const;

export type ColumnId = (typeof columnIds)[number];
export type RowId = (typeof rowIds)[number];

export type FilterStateSnapshot = {
  all: boolean;
  rows: Record<RowId, boolean>;
  cols: Record<ColumnId, boolean>;
  cells: Record<string, boolean>;
};

type FilterChangeListener = (snapshot: FilterStateSnapshot) => void;

export class AircraftFilterEngine {
  private hiddenCells = new Set<string>();
  private listeners = new Set<FilterChangeListener>();

  constructor() {
    try {
      const saved = localStorage.getItem("skopa_map_filters_v3");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          this.hiddenCells = new Set(parsed);
        }
      }
    } catch (e) {
      console.error("Failed to rehydrate map filters:", e);
    }
  }

  public subscribe(listener: FilterChangeListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    const snapshot = this.getSnapshot();
    try {
      localStorage.setItem(
        "skopa_map_filters_v3",
        JSON.stringify(Array.from(this.hiddenCells)),
      );
    } catch {}
    this.listeners.forEach((listener) => listener(snapshot));
  }

  public getSnapshot(): FilterStateSnapshot {
    const rows = {} as Record<RowId, boolean>;
    const cols = {} as Record<ColumnId, boolean>;
    const cells = {} as Record<string, boolean>;

    rowIds.forEach((row) => {
      columnIds.forEach((col) => {
        cells[`${row}-${col}`] = !this.hiddenCells.has(`${row}-${col}`);
      });
    });

    rowIds.forEach((row) => {
      rows[row] = columnIds.some((col) => cells[`${row}-${col}`]);
    });

    columnIds.forEach((col) => {
      cols[col] = rowIds.some((row) => cells[`${row}-${col}`]);
    });

    const all = rowIds.some((row) => rows[row]);

    return { all, rows, cols, cells };
  }

  /**
   * High performance lookup used during WebSocket update parsing
   */
  public isCellVisible(rowId: string, colId: string): boolean {
    return !this.hiddenCells.has(`${rowId}-${colId}`);
  }

  // --- MUTATORS ---
  public toggleAll(): void {
    const snapshot = this.getSnapshot();
    if (snapshot.all) {
      rowIds.forEach((row) => {
        columnIds.forEach((col) => this.hiddenCells.add(`${row}-${col}`));
      });
    } else {
      this.hiddenCells.clear();
    }
    this.emit();
  }

  public toggleRow(rowId: RowId): void {
    const snapshot = this.getSnapshot();
    const targetState = !snapshot.rows[rowId];
    columnIds.forEach((col) => {
      if (targetState) this.hiddenCells.delete(`${rowId}-${col}`);
      else this.hiddenCells.add(`${rowId}-${col}`);
    });
    this.emit();
  }

  public toggleCol(colId: ColumnId): void {
    const snapshot = this.getSnapshot();
    const targetState = !snapshot.cols[colId];
    rowIds.forEach((row) => {
      if (targetState) this.hiddenCells.delete(`${row}-${colId}`);
      else this.hiddenCells.add(`${row}-${colId}`);
    });
    this.emit();
  }

  public toggleCell(rowId: RowId, colId: ColumnId): void {
    const key = `${rowId}-${colId}`;
    if (this.hiddenCells.has(key)) this.hiddenCells.delete(key);
    else this.hiddenCells.add(key);
    this.emit();
  }
}
